import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

export type ThemeMode = "light" | "dark";

interface ThemeState {
  mode: ThemeMode;
}

const getInitialTheme = (): ThemeMode => {
  if (typeof window !== "undefined") {
    const savedTheme = localStorage.getItem("theme") as ThemeMode;
    if (savedTheme) return savedTheme;

    if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }
  }
  return "light";
};

const applyTheme = (theme: ThemeMode) => {
  if (typeof window !== "undefined") {
    document.documentElement.setAttribute("data-theme", theme);
  }
};

const initialState: ThemeState = {
  mode: getInitialTheme(),
};

const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    toggle: (state) => {
      state.mode = state.mode === "light" ? "dark" : "light";
      if (typeof window !== "undefined") {
        localStorage.setItem("theme", state.mode);
        applyTheme(state.mode);
      }
    },
    setTheme: (state, action: PayloadAction<ThemeMode>) => {
      state.mode = action.payload;
      if (typeof window !== "undefined") {
        localStorage.setItem("theme", action.payload);
        applyTheme(action.payload);
      }
    },
    initializeTheme: (state) => {
      const theme = getInitialTheme();
      state.mode = theme;
      if (typeof window !== "undefined") {
        localStorage.setItem("theme", theme);
        applyTheme(theme);
      }
    },
  },
});

export const { toggle, setTheme, initializeTheme } = themeSlice.actions;
export default themeSlice.reducer;
